import { useEffect, useLayoutEffect, useRef, type RefObject } from 'react';

const FOCUSABLE_SELECTOR = 'button, input, select, textarea, a[href], [tabindex]:not([tabindex="-1"])';

/**
 * useDrawerFocus
 *
 * Shared focus + accessibility handling for slide-in drawers that stay mounted off-screen while closed.
 *
 *  - Closed: the panel gets `inert` + `aria-hidden="true"`, so nothing inside is focusable or announced
 *    (a mounted-but-hidden `role="dialog" aria-modal="true"` is otherwise enterable by screen readers).
 *    Set through the DOM because React 18's typings have no `inert` prop.
 *  - Open: remembers the opener (`document.activeElement`) and moves focus into the panel
 *    (first control, else the panel itself).
 *  - Close (Esc, X, backdrop): restores focus to the opener if it is still in the DOM and focus is not
 *    already somewhere meaningful outside the panel (never steals focus the user moved on purpose).
 *
 * Pair with `useEscapeLayer`; render `aria-modal` only while open.
 */
export function useDrawerFocus(isOpen: boolean, panelRef: RefObject<HTMLElement>): void {
  const openerRef = useRef<HTMLElement | null>(null);

  // Layout effect so the closed panel is inert before first paint and in the same commit as the close.
  // On close, focus must leave the panel BEFORE aria-hidden/inert are applied (otherwise the browser
  // warns "Blocked aria-hidden on an element because its descendant retained focus").
  useLayoutEffect(() => {
    const panel = panelRef.current;
    if (!panel) return;
    if (isOpen) {
      panel.removeAttribute('inert');
      panel.removeAttribute('aria-hidden');
      return;
    }
    const active = document.activeElement;
    const insidePanel = active instanceof HTMLElement && panel.contains(active);
    const focusLost = !active || active === document.body || insidePanel;
    const opener = openerRef.current;
    openerRef.current = null;
    if (focusLost && opener && opener.isConnected && opener !== document.body) {
      opener.focus();
    }
    if (document.activeElement instanceof HTMLElement && panel.contains(document.activeElement)) {
      document.activeElement.blur();
    }
    panel.setAttribute('inert', '');
    panel.setAttribute('aria-hidden', 'true');
  }, [isOpen, panelRef]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const panel = panelRef.current;
    if (!panel) return undefined;

    openerRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;

    const candidates = Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
      (el) => !el.matches(':disabled') && el.closest('[aria-hidden="true"], [hidden]') === null,
    );
    let focused = false;
    for (const el of candidates) {
      el.focus();
      if (document.activeElement === el) {
        focused = true;
        break;
      }
    }
    if (!focused) {
      if (!panel.hasAttribute('tabindex')) panel.setAttribute('tabindex', '-1');
      panel.focus();
    }

    // Unmount while open: the layout effect above never sees a close, so restore here.
    return () => {
      const opener = openerRef.current;
      if (!opener) return;
      const active = document.activeElement;
      const focusLost = !active || active === document.body || panel.contains(active);
      if (focusLost && opener.isConnected && opener !== document.body) opener.focus();
      openerRef.current = null;
    };
  }, [isOpen, panelRef]);
}
